/**
 * Auditorias e Testes de Segurança — P1.4.
 * Route: /audits
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DocumentsLink } from '../components/DocumentsLink';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileSearch, Plus, Sparkles, Loader2, Trash2, ClipboardCheck, AlertCircle, Building2, Clock, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { VerifyReviewsButton } from '../components/VerifyReviewsButton';

const TYPE_META = {
  pentest: { label: 'Pentest', tone: 'bg-red-50 text-red-700 border-red-200' },
  internal_audit: { label: 'Auditoria Interna', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  external_audit: { label: 'Auditoria Externa', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  soc2: { label: 'SOC 2', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  iso27001: { label: 'ISO 27001', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  red_team: { label: 'Red Team', tone: 'bg-red-50 text-red-700 border-red-200' },
  vulnerability_assessment: { label: 'VA', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  code_review: { label: 'Code Review', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const STATUS_META = {
  planned: { label: 'Planeada', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'Em curso', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Concluída', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Cancelada', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const SEVERITY_META = {
  critical: { label: 'Crítico', tone: 'bg-red-100 text-red-800 border-red-300' },
  high: { label: 'Alto', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  medium: { label: 'Médio', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { label: 'Baixo', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  info: { label: 'Info', tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const FINDING_STATUS_META = {
  open: { label: 'Aberto', tone: 'bg-red-50 text-red-700 border-red-200' },
  in_progress: { label: 'Em curso', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  resolved: { label: 'Resolvido', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  risk_accepted: { label: 'Risco aceite', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  false_positive: { label: 'Falso positivo', tone: 'bg-slate-100 text-slate-500 border-slate-200' },
};

const emptyForm = {
  name: '', audit_type: 'pentest', scope: '', provider: '',
  start_date: new Date().toISOString().slice(0, 10), end_date: '',
  status: 'planned', report_url: '', executive_summary: '',
  next_audit_date: '',
};

const emptyFinding = {
  title: '', severity: 'medium', description: '', recommendation: '',
  cve_ids: '', affected_assets: '', owner_email: '', due_date: '',
};

export default function Audits() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'ciso';
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState({});
  const [findingDialog, setFindingDialog] = useState(null);
  const [findingForm, setFindingForm] = useState(emptyFinding);

  useEffect(() => { document.title = 'Auditorias · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const [s, l] = await Promise.all([api.get('/audits/stats'), api.get('/audits')]);
        if (ignore) return;
        setStats(s.data);
        setItems(l.data.items || []);
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar.');
      } finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [refresh]);

  const seed = async () => {
    try {
      const { data } = await api.post('/audits/seed');
      toast.success(`${data.inserted} auditorias demo criadas.`);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name, audit_type: a.audit_type, scope: a.scope, provider: a.provider || '',
      start_date: a.start_date, end_date: a.end_date || '', status: a.status,
      report_url: a.report_url || '', executive_summary: a.executive_summary || '',
      next_audit_date: a.next_audit_date || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name || !form.scope) { toast.error('Nome e âmbito são obrigatórios.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || null };
      if (editing) {
        const { audit_type: _unusedType, ...rest } = payload;
        await api.patch(`/audits/${editing.audit_id}`, rest);
      } else {
        await api.post('/audits', payload);
      }
      toast.success(editing ? 'Atualizado.' : 'Criado.');
      setShowForm(false);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remover ${a.name}?`)) return;
    try {
      await api.delete(`/audits/${a.audit_id}`);
      toast.success('Removido.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const openFinding = (a) => { setFindingDialog(a); setFindingForm(emptyFinding); };

  const submitFinding = async () => {
    if (!findingForm.title || !findingForm.description) { toast.error('Título e descrição são obrigatórios.'); return; }
    try {
      const payload = {
        ...findingForm,
        cve_ids: findingForm.cve_ids.split(',').map(s => s.trim()).filter(Boolean),
        affected_assets: findingForm.affected_assets.split(',').map(s => s.trim()).filter(Boolean),
        due_date: findingForm.due_date || null,
      };
      await api.post(`/audits/${findingDialog.audit_id}/findings`, payload);
      toast.success('Finding registado.');
      setFindingDialog(null);
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  const updateFindingStatus = async (auditId, findingId, newStatus) => {
    try {
      await api.patch(`/audits/${auditId}/findings/${findingId}`, { status: newStatus });
      toast.success('Estado atualizado.');
      setRefresh(r => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Falha.'); }
  };

  return (
    <Layout title="Auditorias & Testes de Segurança">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="audits-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <FileSearch className="h-6 w-6 text-indigo-600" /> Auditorias & Testes de Segurança
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Pentests, auditorias internas/externas e certificações (SOC 2, ISO 27001). Evidência obrigatória para <strong>NIS2 Art. 21(2)(b) e (f)</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && <VerifyReviewsButton />}
            {canEdit && stats?.total === 0 && (
              <Button variant="outline" onClick={seed} data-testid="audits-seed-btn"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Popular demo</Button>
            )}
            {canEdit && <Button onClick={openCreate} data-testid="audits-new-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Nova auditoria</Button>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { key: 'total', label: 'Auditorias', value: stats?.total ?? 0, icon: Building2, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'in_progress', label: 'Em curso', value: stats?.in_progress ?? 0, icon: Clock, tone: 'from-blue-500 to-blue-600' },
            { key: 'open_findings', label: 'Findings abertos', value: stats?.open_findings ?? 0, icon: AlertCircle, tone: 'from-amber-500 to-amber-600' },
            { key: 'critical_open', label: 'Críticos abertos', value: (stats?.critical_open ?? 0) + (stats?.high_open ?? 0), icon: AlertCircle, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`audits-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}><k.icon className="h-4 w-4 text-white" /></div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-0">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              items.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem auditorias. Use &quot;Popular demo&quot; ou &quot;Nova auditoria&quot;.</div> :
              <div className="divide-y divide-slate-100">
                {items.map(a => {
                  const tm = TYPE_META[a.audit_type] || {};
                  const sm = STATUS_META[a.status] || {};
                  const findings = a.findings || [];
                  const open = findings.filter(f => f.status === 'open' || f.status === 'in_progress').length;
                  const isExp = expanded[a.audit_id];
                  return (
                    <div key={a.audit_id} data-testid={`audit-${a.audit_id}`}>
                      <div className="p-4 hover:bg-slate-50">
                        <div className="flex items-start gap-4">
                          <Button variant="ghost" size="sm" className="mt-0.5" onClick={() => setExpanded({ ...expanded, [a.audit_id]: !isExp })}>
                            {isExp ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </Button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h3 className="font-semibold text-slate-900">{a.name}</h3>
                              <Badge variant="secondary" className={tm.tone}>{tm.label}</Badge>
                              <Badge variant="secondary" className={sm.tone}>{sm.label}</Badge>
                              {open > 0 && <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200 text-[10px]">{open} finding{open !== 1 ? 's' : ''} aberto{open !== 1 ? 's' : ''}</Badge>}
                            </div>
                            <p className="text-sm text-slate-600 line-clamp-2">{a.scope}</p>
                            <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                              {a.provider && <span><strong>Provider:</strong> {a.provider}</span>}
                              <span><strong>Início:</strong> {a.start_date}</span>
                              {a.end_date && <span><strong>Fim:</strong> {a.end_date}</span>}
                              <span>· {findings.length} finding{findings.length !== 1 ? 's' : ''}</span>
                            </div>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <DocumentsLink module="audit" entityId={a.audit_id} title={a.name} />
                              <Button variant="ghost" size="sm" onClick={() => openFinding(a)} data-testid={`audit-add-finding-${a.audit_id}`}><Plus className="h-3.5 w-3.5" /> Finding</Button>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(a)} data-testid={`audit-edit-${a.audit_id}`}>Editar</Button>
                              {user?.role === 'admin' && <Button variant="ghost" size="sm" onClick={() => remove(a)}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>}
                            </div>
                          )}
                        </div>
                      </div>
                      {isExp && (
                        <div className="bg-slate-50 border-t border-slate-100 p-4 space-y-3">
                          {a.executive_summary && (
                            <div>
                              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Sumário executivo</div>
                              <p className="text-sm text-slate-700">{a.executive_summary}</p>
                            </div>
                          )}
                          {findings.length === 0 ? (
                            <p className="text-sm text-slate-400 italic">Sem findings registados.</p>
                          ) : (
                            <div className="space-y-2">
                              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Findings ({findings.length})</div>
                              {findings.map(f => {
                                const sv = SEVERITY_META[f.severity] || {};
                                const fs = FINDING_STATUS_META[f.status] || {};
                                return (
                                  <div key={f.finding_id} className="bg-white border border-slate-200 rounded-lg p-3" data-testid={`finding-${f.finding_id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                          <span className="font-medium text-slate-900 text-sm">{f.title}</span>
                                          <Badge variant="secondary" className={`text-[10px] ${sv.tone}`}>{sv.label}</Badge>
                                          <Badge variant="secondary" className={`text-[10px] ${fs.tone}`}>{fs.label}</Badge>
                                        </div>
                                        <p className="text-xs text-slate-600">{f.description}</p>
                                        {f.recommendation && <p className="text-xs text-slate-500 mt-1"><strong>Recomendação:</strong> {f.recommendation}</p>}
                                      </div>
                                      {canEdit && f.status !== 'resolved' && (
                                        <Select value={f.status} onValueChange={(v) => updateFindingStatus(a.audit_id, f.finding_id, v)}>
                                          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`finding-status-${f.finding_id}`}><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            {Object.entries(FINDING_STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            }
          </CardContent>
        </Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar: ${editing.name}` : 'Nova auditoria'}</DialogTitle>
            <DialogDescription>Registe uma auditoria/teste com âmbito e cronograma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="audit-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.audit_type} onValueChange={v => setForm({ ...form, audit_type: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="audit-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYPE_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="audit-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Âmbito *</Label><Textarea rows={2} value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} data-testid="audit-form-scope" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Provider</Label><Input value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} /></div>
              <div><Label>URL do relatório</Label><Input value={form.report_url} onChange={e => setForm({ ...form, report_url: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data início *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Data fim</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div>
              <Label>Próxima auditoria</Label>
              <Input
                type="date"
                value={form.next_audit_date || ''}
                onChange={e => setForm({ ...form, next_audit_date: e.target.value })}
                data-testid="audit-form-next-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Define a data prevista para a próxima realização. O sistema criará automaticamente tarefa e alerta 90/30/7/1 dias antes.
              </p>
            </div>
            <div><Label>Sumário executivo</Label><Textarea rows={3} value={form.executive_summary} onChange={e => setForm({ ...form, executive_summary: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} data-testid="audit-form-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : editing ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!findingDialog} onOpenChange={(o) => { if (!o) setFindingDialog(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Novo finding · {findingDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={findingForm.title} onChange={e => setFindingForm({ ...findingForm, title: e.target.value })} data-testid="finding-form-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Severidade</Label>
                <Select value={findingForm.severity} onValueChange={v => setFindingForm({ ...findingForm, severity: v })}>
                  <SelectTrigger data-testid="finding-form-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SEVERITY_META).map(([v, m]) => <SelectItem key={v} value={v}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Data limite</Label><Input type="date" value={findingForm.due_date} onChange={e => setFindingForm({ ...findingForm, due_date: e.target.value })} /></div>
            </div>
            <div><Label>Descrição *</Label><Textarea rows={3} value={findingForm.description} onChange={e => setFindingForm({ ...findingForm, description: e.target.value })} data-testid="finding-form-desc" /></div>
            <div><Label>Recomendação</Label><Textarea rows={2} value={findingForm.recommendation} onChange={e => setFindingForm({ ...findingForm, recommendation: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CVE IDs (vírgula)</Label><Input value={findingForm.cve_ids} onChange={e => setFindingForm({ ...findingForm, cve_ids: e.target.value })} placeholder="CVE-2024-1234" /></div>
              <div><Label>Owner email</Label><Input value={findingForm.owner_email} onChange={e => setFindingForm({ ...findingForm, owner_email: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFindingDialog(null)}>Cancelar</Button>
            <Button onClick={submitFinding} data-testid="finding-form-submit">Registar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
